/** Cubic coefficients decoded from the captured Unity animation clips.
 * Evaluate in seconds from the key, in Unity canvas units. See react-port.md. */
interface RewardCurveKey {
    time: number;
    polynomial: readonly [number, number, number, number];
}
type RewardCurve = readonly RewardCurveKey[];

export const REWARD_CURVES = {
    reveal: [
        {
            time: 0.0,
            polynomial: [-153.60000610351562, 57.60000228881836, 0.0, 0.0],
        },
        {
            time: 0.25,
            polynomial: [
                25.600006103515625, -9.60000228881836, 0.0, 1.2000000476837158,
            ],
        },
        {
            time: 0.5,
            polynomial: [0.0, 0.0, 0.0, 1.0],
        },
    ],
    rayScale: [
        {
            time: 0.0,
            polynomial: [
                0.00046296310028992593, -0.0027777785435318947,
                -0.016666671261191368, 1.0,
            ],
        },
        {
            time: 6.0,
            polynomial: [
                -0.0011574076488614082, 0.009722224436700344, 0.0,
                0.8999999761581421,
            ],
        },
        {
            time: 12.0,
            polynomial: [
                0.0015542329056188464, -0.013492063619196415,
                -0.008333330042660236, 1.0,
            ],
        },
        {
            time: 18.0,
            polynomial: [
                -0.0006316812941804528, 0.008843536488711834,
                -0.00238095223903656, 0.800000011920929,
            ],
        },
        {
            time: 25.0,
            polynomial: [0.0, 0.0, 0.0, 1.0],
        },
    ],
    rayRotation: [
        {
            time: 0.0,
            polynomial: [0.0, 0.0, -14.359999656677246, 0.0],
        },
        {
            time: 25.0,
            polynomial: [0.0, 0.0, 0.0, -359.0],
        },
    ],
    exitOpacity: [
        {
            time: 0.0,
            polynomial: [0.0, 0.0, 0.0, 1.0],
        },
        {
            time: 0.5,
            polynomial: [74.07406616210938, -33.33333206176758, 0.0, 1.0],
        },
        {
            time: 0.800000011920929,
            polynomial: [0.0, 0.0, 0.0, 0.0],
        },
    ],
    exitItemY: [
        {
            time: 0.0,
            polynomial: [0.0, 0.0, 0.0, 0.0],
        },
        {
            time: 0.28333333134651184,
            polynomial: [20381.427734375, -4842.01171875, 0.0, 0.0],
        },
        {
            time: 0.5,
            polynomial: [
                4069.43505859375, 1660.793701171875, 772.179443359375, -20.0,
            ],
        },
        {
            time: 0.800000011920929,
            polynomial: [0.0, 0.0, 0.0, 471.0],
        },
    ],
    exitGlowY: [
        {
            time: 0.0,
            polynomial: [0.0, 0.0, 0.0, 0.0],
        },
        {
            time: 0.28333333134651184,
            polynomial: [20381.427734375, -4842.01171875, 0.0, 0.0],
        },
        {
            time: 0.5,
            polynomial: [
                4419.537109375, 1555.763671875, 772.179443359375, -20.0,
            ],
        },
        {
            time: 0.800000011920929,
            polynomial: [0.0, 0.0, 0.0, 471.0],
        },
    ],
} as const satisfies Record<string, RewardCurve>;

export function sampleRewardCurve(curve: RewardCurve, seconds: number): number {
    let segment = curve[0];
    if (segment === undefined) return 0;
    for (const key of curve) {
        if (key.time > seconds) break;
        segment = key;
    }
    const dt = Math.max(0, seconds - segment.time);
    const [a, b, c, d] = segment.polynomial;
    return ((a * dt + b) * dt + c) * dt + d;
}
